// @flow

import React, { useEffect, useState } from 'react';


import { createInviteDialogEvent, sendAnalytics } from '../../../analytics';
import { getRoomName, setPassword as setPass } from '../../../base/conference';
import { getInviteURL } from '../../../base/connection';
import { Dialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import { getLocalParticipant, isLocalParticipantModerator } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { _getDefaultPhoneNumber, getInviteText,
    isAddPeopleEnabled, isDialOutEnabled, updateDialInNumbers } from '../../../invite';
import CopyMeetingLinkSection from '../../../invite/components/add-people-dialog/web/CopyMeetingLinkSection';
import DialInSection from '../../../invite/components/add-people-dialog/web/DialInSection';
import InviteByEmailSection from '../../../invite/components/add-people-dialog/web/InviteByEmailSection';
import { LobbySection } from '../../../lobby';
import PasswordSection from '../../../security/components/security-dialog/PasswordSection';

import Header from './Header';

declare var interfaceConfig: Object;

type Props = {

    /**
     * Whether or not the current user can modify the current password.
     */
    _canEditPassword: boolean,

    /**
     * The MHConference for which to display a lock state and change the
     * password.
     */
    _conference: Object,

    /**
     * The value for how the conference is locked (or undefined if not locked)
     * as defined by room-lock constants.
     */
    _locked: string,

    /**
     * The current known password for the MHConference.
     */
    _password: string,

    /**
     * The number of digits to be used in the password.
     */
    _passwordNumberOfDigits: ?number,

    /**
     * Indicates whether e2ee will be displayed or not.
     */
    _showE2ee: boolean,

    /**
     * Action that sets the conference password.
     */
    setPassword: Function,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    /**
     * The object representing the dialIn feature.
     */
    _dialIn: Object,

    /**
     * Whether or not embed meeting should be visible.
     */
    _embedMeetingVisible: boolean,

    /**
     * Whether or not invite contacts should be visible.
     */
    _inviteContactsVisible: boolean,

    /**
     * The current url of the conference to be copied onto the clipboard.
     */
    _inviteUrl: string,

    /**
     * The current location url of the conference.
     */
     _phoneNumber: ?string,

    /**
     * Method to update the dial in numbers.
     */
    updateNumbers: Function,

    /**
     * Invitation text.
     */
     _invitationText: Object
}

/**
 * Represents a replacement for the subject, prompting the
 * sole participant to invite more participants.
 *
 * @param {Object} props - The props of the component.
 * @returns {React$Element<any>}
 */
function ConfigureYourMeeting({
    _canEditPassword,
    _conference,
    _locked,
    _password,
    _passwordNumberOfDigits,
    setPassword,
    _dialIn,
    _inviteUrl,
    _phoneNumber,
    updateNumbers,
    t,
    _invitationText
}: Props) {
    const [ passwordEditEnabled, setPasswordEditEnabled ] = useState(false);
    const [ phoneNumber, setPhoneNumber ] = useState(undefined);


    useEffect(() => {
        if (passwordEditEnabled && _password) {
            setPasswordEditEnabled(false);
        }
        sendAnalytics(createInviteDialogEvent(
             'invite.dialog.opened', 'dialog'));

        return () => {
            sendAnalytics(createInviteDialogEvent(
                 'invite.dialog.closed', 'dialog'));
        };
    }, [ _password ]);


    const inviteSubject = t('addPeople.inviteMoreMailSubject', {
        appName: interfaceConfig.APP_NAME
    });

    /**
     * Updates the phone number in the state once the dial-in numbers are fetched.
     *
     * @returns {void}
     */
    useEffect(() => {
        if (!phoneNumber && _dialIn && _dialIn.numbers) {
            setPhoneNumber(_getDefaultPhoneNumber(_dialIn.numbers));
        }
    }, [ _dialIn ]);

    /**
     * Updates the dial-in numbers.
     */
    useEffect(() => {
        if (!_dialIn.numbers) {
            updateNumbers();
        }
    }, []);

    return (
        <Dialog
            cancelKey = { 'dialog.close' }
            customHeader = { Header }
            disableOverlay = { true }
            submitDisabled = { true }
            width = 'medium' >
            <div className = 'rows'>
                <div className = 'col-m-5' >
                    <div className = 'security-dialog'>
                        <LobbySection />
                        <PasswordSection
                            canEditPassword = { _canEditPassword }
                            conference = { _conference }
                            locked = { _locked }
                            password = { _password }
                            passwordEditEnabled = { passwordEditEnabled }
                            passwordNumberOfDigits = { _passwordNumberOfDigits }
                            setPassword = { setPassword }
                            setPasswordEditEnabled = { setPasswordEditEnabled } />

                    </div>

                </div>
                <div className = 'col-m-1'>
                    <div className = 'vertical-row' />
                </div>
                <div className = 'col-m-6 '>
                    <div className = 'invite-more-dialog'>

                        <CopyMeetingLinkSection url = { _inviteUrl } />
                        <InviteByEmailSection
                            inviteSubject = { inviteSubject }
                            inviteText = { _invitationText }
                            isPassword = { Boolean(_password) } />
                        {/* { _embedMeetingVisible && <EmbedMeetingTrigger /> } */}
                        {/* <div className = 'invite-more-dialog separator' /> */}
                        {
                            _phoneNumber
                        && <DialInSection phoneNumber = { _phoneNumber } />
                        }
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code SecurityDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The ownProps.
 * @private
 * @returns {Props}
 */
function mapStateToProps(state, ownProps) {
    const {
        conference,
        locked,
        password
    } = state['features/base/conference'];
    const { roomPasswordNumberOfDigits } = state['features/base/config'];
    const localParticipant = getLocalParticipant(state);
    const { iAmRecorder } = state['features/base/config'];
    const addPeopleEnabled = isAddPeopleEnabled(state);
    const dialOutEnabled = isDialOutEnabled(state);
    const hideInviteContacts = iAmRecorder || (!addPeopleEnabled && !dialOutEnabled);
    const dialIn = state['features/invite'];
    const phoneNumber = dialIn && dialIn.numbers ? _getDefaultPhoneNumber(dialIn.numbers) : undefined;
    const invitationText = getInviteText({ state,
        phoneNumber,
        _password: password,
        t: ownProps.t });

    return {
        _canEditPassword: isLocalParticipantModerator(state),
        _conference: conference,
        _dialIn: state['features/invite'],
        _locked: locked,
        _password: password,
        _passwordNumberOfDigits: roomPasswordNumberOfDigits,
        _conferenceName: getRoomName(state),
        _inviteContactsVisible: interfaceConfig.ENABLE_DIAL_OUT && !hideInviteContacts,
        _inviteUrl: getInviteURL(state),
        _invitationText: invitationText,
        _localParticipantName: localParticipant?.name,
        _locationUrl: state['features/base/connection'].locationURL,
        _phoneNumber: phoneNumber
    };
}

const mapDispatchToProps = { setPassword: setPass,
    updateNumbers: () => updateDialInNumbers()
};


export default translate(connect(mapStateToProps, mapDispatchToProps)(ConfigureYourMeeting));
